import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import httpMethod from 'services/update2025/httpMethod';

import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { TriStateCheckbox } from "primereact/tristatecheckbox";
import { Dropdown } from "primereact/dropdown";

import { Can } from "configs/CASLContext";
import { deleteRole, deleteRoles, findRoles } from "services/RoleService";
import { RoleContext } from "components/role/RoleContext";
import { Confirmation } from "components/Confirmation";
import { RoleEditor } from "components/role/RoleEditor";
import { create, del, edit, Role, X_TOTAL_COUNT } from "utils/constants";
import { handleError } from "utils/handleError";

export const RoleList = () => {
    const toast = useRef(null);
    const dt = useRef(null);
    const { t, i18n } = useTranslation(["role", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const roleContext = useContext(RoleContext);
    const { role, resetRole, setRole, roles, setRoles, setSubmitted, setRoleDialog } = roleContext;

    const [selectedRoles, setSelectedRoles] = useState([]);
    const [deleteRoleDialog, setDeleteRoleDialog] = useState(false);
    const [deleteRolesDialog, setDeleteRolesDialog] = useState(false);

    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
        filters: {
            code: { value: "", matchMode: "contains" },
            name: { value: "", matchMode: "contains" },
            type: { value: null, matchMode: "equals" },
            enabled: { value: null, matchMode: "equals" },
        },
    });
    const [globalFilter, setGlobalFilter] = useState(null);

    const roleTypes = [
        { label: t("officer"), value: "OFFICER" },
        { label: t("citizen"), value: "CITIZEN" },
        { label: t("foreign"), value: "FOREIGN" },
        { label: t("client"), value: "CLIENT" },
    ];

    useEffect(() => {
        loadLazyData();
    }, [lazyParams]);

    const loadLazyData = () => {
        setLoading(true);

        findRoles(headers, lazyParams)
            .then((res) => {
                setTotalRecords(res.headers[X_TOTAL_COUNT] ? parseInt(res.headers[X_TOTAL_COUNT]) : 0);
                setRoles(res.data);
            })
            .catch((error) => handleError(toast, t, error))
            .finally(() => setLoading(false));
    };

    const onPage = (event) => {
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        let _lazyParams = { ...lazyParams, ...event };
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        let _lazyParams = { ...lazyParams, ...event };
        _lazyParams["first"] = 0;
        setLazyParams(_lazyParams);
    };

    const openNewDialog = () => {
        resetRole();
        setSubmitted(false);
        setRoleDialog(true);
    };

    const openEditDialog = (role) => {
        setRole({ ...role });
        setRoleDialog(true);
    };

    const hideDeleteRolesDialog = () => {
        setDeleteRolesDialog(false);
    };

    const confirmDeleteRole = (role) => {
        setRole(role);
        setDeleteRoleDialog(true);
    };

    const hideDeleteRoleDialog = () => {
        setDeleteRoleDialog(false);
    };

    const confirmDeleteSelected = () => {
        setDeleteRolesDialog(true);
    };

    const deleteRoleAction = () => {
        deleteRole(headers, role.id)
            .then(() => {
                let _roles = roles.filter((val) => val.id !== role.id);
                setRoles(_roles);
                setDeleteRoleDialog(false);
                resetRole();
                toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.deleted", { ns: "common", object: t("role") }), life: 3000 });
            })
            .catch((error) => handleError(toast, t, error));
    };

    const deleteSelectedRolesAction = () => {
        let ids = selectedRoles.map((r) => r.id);
        deleteRoles(headers, ids)
            .then(() => {
                let _roles = roles.filter((val) => !selectedRoles.includes(val));
                setRoles(_roles);
                setDeleteRolesDialog(false);
                setSelectedRoles(null);
                toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.deleted", { ns: "common", object: t("roles") }), life: 3000 });
            })
            .catch((error) => handleError(toast, t, error));
    };

    const checkboxFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <TriStateCheckbox value={options.value} onChange={(e) => options.filterApplyCallback(e.value)} />
            </div>
        );
    };

    const textFilterTemplate = (options) => {
        return (
            <div className="flex align-items-center justify-content-center">
                <InputText value={options.value} onChange={(e) => options.filterApplyCallback(e.target?.value || "")} />
            </div>
        );
    };

    const roleTypeFilterTemplate = (options) => {
        return <Dropdown value={options.value} options={roleTypes} onChange={(e) => options.filterApplyCallback(e.value)} />;
    };

    const leftToolbarTemplate = () => {
        return <React.Fragment></React.Fragment>;
    };

    const headerTemplate = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{t("role.list")}</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(e) => setGlobalFilter(e.target.value)} placeholder={t("search", { ns: "common" })} />
            </span>
        </div>
    );

    const roleTypeBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">
                    <p>{t(String(rowData.type).toLowerCase())}</p>
                </div>
            </>
        );
    };

    const checkboxBodyTemplate = (rowData) => {
        return (
            <>
                <div className="flex align-items-center justify-content-center">
                    <Checkbox checked={rowData.enabled} readOnly />
                </div>
            </>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex align-items-center justify-content-center actions">
                <Can I={edit} a={Role}>
                    <Button icon="pi pi-pencil" className="p-button-rounded p-button mr-2" tooltip={t("edit", { ns: "common" })} onClick={() => openEditDialog(rowData)} />
                </Can>
                <Can I={del} a={Role}>
                    <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" tooltip={t("delete", { ns: "common" })} onClick={() => confirmDeleteRole(rowData)} />
                </Can>
            </div>
        );
    };

    const paginatorTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink",
        RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 20, value: 20 },
                { label: 30, value: 30 },
                { label: 50, value: 50 },
            ];

            return (
                <React.Fragment>
                    <div style={{ width: "140px", display: "inline-block" }}>
                        <span className="mx-1">{t("show", { ns: "common" })}: </span>
                        <Dropdown value={options.value} options={dropdownOptions} onChange={options.onChange} />
                    </div>
                </React.Fragment>
            );
        },
        CurrentPageReport: (options) => {
            return <span style={{ width: "calc(100% - 490px)", textAlign: "center", display: "inline-block" }}>{t("total", { ns: "common", total: options.totalRecords })}</span>;
        },
    };

    return (
        <div className="grid crud">
            <div className="col-12">
                <div className="card">
                    <div className="grid">
                        <div className="col-12">
                            <h4 className="text-center font-bold">{t("role.title")?.toUpperCase()}</h4>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12">
                            <Toast ref={toast} />
                            <DataTable
                                ref={dt}
                                value={roles}
                                selection={selectedRoles}
                                onSelectionChange={(e) => setSelectedRoles(e.value)}
                                dataKey="id"
                                className="datatable-responsive p-datatable-sm"
                                globalFilter={globalFilter}
                                header={headerTemplate}
                                showGridlines
                                lazy
                                paginator
                                rows={lazyParams.rows}
                                rowsPerPageOptions={[10, 20, 50]}
                                first={lazyParams.first}
                                totalRecords={totalRecords}
                                onPage={onPage}
                                onSort={onSort}
                                sortField={lazyParams.sortField}
                                sortOrder={lazyParams.sortOrder}
                                onFilter={onFilter}
                                filters={lazyParams.filters}
                                loading={loading}
                                paginatorClassName="justify-content-start"
                                paginatorTemplate={paginatorTemplate}
                                emptyMessage="Không có dữ liệu"
                                scrollable
                            >
                                <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} style={{ maxWidth: "50px" }} />
                                <Column field="code" header={t("code")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                <Column field="name" header={t("name")} sortable filter showFilterMatchModes={false} filterElement={textFilterTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                <Column field="type" header={t("type")} sortable filter showFilterMatchModes={false} filterElement={roleTypeFilterTemplate} body={roleTypeBodyTemplate} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                <Column
                                    field="enabled"
                                    header={t("enabled")}
                                    dataType="boolean"
                                    sortable
                                    filter
                                    filterElement={checkboxFilterTemplate}
                                    body={checkboxBodyTemplate}
                                    headerStyle={{ width: "10rem" }}
                                    style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }}
                                />
                                <Column field="modifiedDate" header={t("modifiedDate", { ns: "common" })} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                <Column field="modifiedBy.username" header={t("modifiedBy", { ns: "common" })} style={{ minWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                                <Column body={actionBodyTemplate} style={{ maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "normal" }} />
                            </DataTable>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12 tex-center mt-2" style={{ display: "flex", justifyContent: "center" }}>
                            <Can I={create} a={Role}>
                                <Button label={t("new", { ns: "common" })} icon="pi pi-plus" className="p-button mr-2" onClick={openNewDialog} />
                            </Can>
                            {selectedRoles?.length > 0 && (
                                <Can I={del} a={Role}>
                                    <Button label={t("delete", { ns: "common" })} icon="pi pi-trash" className="p-button-danger" onClick={confirmDeleteSelected} />
                                </Can>
                            )}
                        </div>
                    </div>
                    <Confirmation
                        visible={deleteRoleDialog}
                        hideAction={hideDeleteRoleDialog}
                        processAction={deleteRoleAction}
                        confirmMessage={
                            role && (
                                <span>
                                    {t("confirm.delete", { ns: "common", object: t("role") })} <b>{role.name}</b>?
                                </span>
                            )
                        }
                    />
                    <Confirmation visible={deleteRolesDialog} hideAction={hideDeleteRolesDialog} processAction={deleteSelectedRolesAction} confirmMessage={selectedRoles && <span>{t("confirm.delete.selected", { ns: "common", object: t("roles") })}</span>} />
                    <RoleEditor />
                </div>
            </div>
        </div>
    );
};
